import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import * as ROUTES from '../constants/routes';
import { useAuthListener } from '../hooks';
import { clearAuthData, getAuthUser, isAuthenticated } from '../utils';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_VIDEO_HIGHLIGHT_API || 'https://extractor-highlight-video.lumination.vn';
const MAX_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;

// API Helper Functions
const apiCall = async (endpoint, options = {}, userEmail) => {
  const headers = {
    'email': userEmail || 'admin@gmail.com',
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.statusText}`);
  }

  // Check if response is JSON
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response;
};

const uploadVideo = async (file, userEmail, category, scriptFile = null) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  if (category === 'phim' && scriptFile) {
    formData.append('script_file', scriptFile);
  }

  const headers = {
    'email': userEmail || 'admin@gmail.com',
  };

  const response = await fetch(`${API_BASE_URL}/jobs`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  return response.json();
};

const getJobStatus = (jobId, userEmail) => apiCall(`/jobs/${jobId}/status`, {}, userEmail);

const getJobResult = (jobId, userEmail) => apiCall(`/jobs/${jobId}/result`, {}, userEmail);

const getJobHistory = (userEmail) => apiCall('/jobs', {}, userEmail);

const deleteJob = (jobId, userEmail) => apiCall(`/jobs/${jobId}`, {
  method: 'DELETE',
}, userEmail);

const getFileUrl = async (path, userEmail) => {
  const encodedPath = encodeURIComponent(path);
  const response = await fetch(`${API_BASE_URL}/files?path=${encodedPath}`, {
    headers: {
      'email': userEmail || 'admin@gmail.com',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch file');
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};


const DashboardGlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght@400&display=swap');

  :root {
    --bg: #08111f;
    --bg-elevated: rgba(11, 24, 43, 0.78);
    --panel: rgba(12, 23, 39, 0.92);
    --panel-strong: #0e1e34;
    --line: rgba(255, 255, 255, 0.08);
    --line-soft: rgba(255, 255, 255, 0.05);
    --text: #f8fbff;
    --muted: #9eb0c7;
    --subtle: #6e8199;
    --accent: #93c5fd;
    --accent-strong: #3b82f6;
    --accent-ink: #0b2f66;
    --blue: #78a8ff;
    --amber: #ffd98e;
    --danger: #ff9e9e;
  }

  * {
    box-sizing: border-box;
  }

  body {
    margin: 0;
    font-family: 'Manrope', sans-serif;
    background: var(--bg);
    color: var(--text);
  }

  button,
  input {
    font-family: inherit;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top right, rgba(120, 168, 255, 0.2), transparent 24%),
    radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.16), transparent 28%),
    linear-gradient(180deg, #09111d 0%, #07101b 100%);
  color: var(--text);
`;

const Icon = styled.span.attrs(() => ({
  className: 'material-symbols-outlined',
}))`
  font-family: 'Material Symbols Outlined';
  font-size: ${({ $size = 22 }) => `${$size}px`};
  font-variation-settings: 'FILL' ${({ $fill = 0 }) => $fill}, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  line-height: 1;
`;

const Sidebar = styled.aside`
  position: fixed;
  left: 0;
  top: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 272px;
  height: 100vh;
  padding: 26px 14px;
  background: rgba(8, 17, 31, 0.9);
  border-right: 1px solid var(--line-soft);
  backdrop-filter: blur(18px);

  @media (max-width: 980px) {
    position: static;
    width: 100%;
    height: auto;
    border-right: 0;
    border-bottom: 1px solid var(--line-soft);
  }
`;

const Brand = styled.div`
  padding: 0 14px;
`;

const BrandLabel = styled.div`
  color: var(--subtle);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const BrandTitle = styled.div`
  margin-top: 8px;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.05em;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 14px;
  cursor: pointer;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 14px;
`;

const HomeButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 1px solid var(--line-soft);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Avatar = styled.img`
  width: ${({ $size = 40 }) => `${$size}px`};
  height: ${({ $size = 40 }) => `${$size}px`};
  object-fit: cover;
  border-radius: ${({ $rounded = 14 }) => `${$rounded}px`};
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: #142239;
`;

const UserMeta = styled.div`
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 700;
`;

const UserHint = styled.div`
  margin-top: 6px;
  color: var(--subtle);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 13px 16px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(147, 197, 253, 0.18)' : 'transparent')};
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'rgba(147, 197, 253, 0.08)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text)' : 'var(--muted)')};
  font: inherit;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    color: var(--text);
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NewAnalysisButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 12px 18px;
  padding: 15px;
  border: 0;
  border-radius: 18px;
  background: linear-gradient(135deg, #bfdbfe 0%, #3b82f6 100%);
  color: var(--accent-ink);
  font: inherit;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 18px 40px rgba(59, 130, 246, 0.2);
`;

const Main = styled.main`
  margin-left: 272px;
  min-height: 100vh;

  @media (max-width: 980px) {
    margin-left: 0;
  }
`;

const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 15;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 0 28px;
  height: 74px;
  background: rgba(8, 17, 31, 0.74);
  border-bottom: 1px solid var(--line-soft);
  backdrop-filter: blur(18px);

  @media (max-width: 720px) {
    padding: 0 16px;
  }
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;

  @media (max-width: 720px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--line-soft);
`;

const TopTab = styled.button`
  padding: 10px 16px;
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? 'rgba(147, 197, 253, 0.12)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--text)' : 'var(--muted)')};
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const SearchWrap = styled.label`
  position: relative;
  display: ${({ $show }) => ($show ? 'flex' : 'none')};
  align-items: center;
  width: min(320px, 100%);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px 12px 42px;
  border: 1px solid var(--line-soft);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.03);
  color: var(--text);
  font-size: 14px;

  &::placeholder {
    color: var(--subtle);
  }
`;

const SearchIcon = styled(Icon)`
  position: absolute;
  left: 14px;
  color: var(--subtle);
`;

const TopActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;

  @media (max-width: 720px) {
    gap: 8px;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--line-soft);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  color: var(--muted);
  cursor: pointer;

  &:hover {
    color: var(--text);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const Content = styled.div`
  padding: 34px 32px 44px;

  @media (max-width: 720px) {
    padding: 22px 16px 32px;
  }
`;

const Panel = styled.section`
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: ${({ $radius = 28 }) => `${$radius}px`};
  box-shadow: 0 26px 60px rgba(0, 0, 0, 0.24);
`;

const HeroSection = styled.div`
  margin-bottom: 28px;
`;

const Eyebrow = styled.div`
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.24em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 14px 0 12px;
  font-size: clamp(34px, 4vw, 50px);
  line-height: 0.94;
  letter-spacing: -0.06em;
`;

const TitleSocial = styled.h3`
  margin: 0 0 12px;
  font-size: 32px;
  line-height: 0.94;
  letter-spacing: -0.06em;
`;

const Subtitle = styled.p`
  max-width: 760px;
  margin: 0;
  color: var(--muted);
  font-size: 16px;
  line-height: 1.75;
`;

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 24px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const CompactMetric = styled(Panel)`
  padding: 20px 22px;
  background: rgba(255, 255, 255, 0.03);
`;

const UploadGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(320px, 0.95fr);
  gap: 24px;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`;

const UploadCanvas = styled(Panel)`
  position: relative;
  min-height: 440px;
  overflow: hidden;
  border: 1px solid rgba(120, 168, 255, 0.2);
  background: radial-gradient(circle at top right, rgba(120, 168, 255, 0.16), transparent 28%),
    radial-gradient(circle at bottom left, rgba(59, 130, 246, 0.14), transparent 30%),
    linear-gradient(180deg, rgba(12, 23, 39, 0.96), rgba(9, 19, 33, 0.98));
`;

const CanvasGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 280px;
    height: 280px;
    border-radius: 50%;
    filter: blur(120px);
    opacity: 0.3;
  }

  &::before {
    top: -80px;
    right: -40px;
    background: rgba(120, 168, 255, 0.42);
  }

  &::after {
    left: -50px;
    bottom: -100px;
    background: rgba(59, 130, 246, 0.34);
  }
`;

const UploadInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 440px;
  padding: 42px;
  text-align: center;
`;

const UploadIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--accent);
  animation: ${float} 4s ease-in-out infinite;
`;

const UploadHeading = styled.h2`
  max-width: 560px;
  margin: 24px 0 12px;
  font-size: clamp(28px, 4vw, 42px);
  line-height: 1.02;
  letter-spacing: -0.05em;
`;

const UploadText = styled.p`
  max-width: 560px;
  margin: 0;
  color: var(--muted);
  line-height: 1.8;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border: 0;
  border-radius: 16px;
  margin-top: 28px;
  background: linear-gradient(135deg, #bfdbfe 0%, #3b82f6 100%);
  color: var(--accent-ink);
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 16px 34px rgba(59, 130, 246, 0.2);
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 18px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const UploadHelper = styled.div`
  margin-top: 18px;
  color: var(--subtle);
  font-size: 12px;
`;

const ProcessingCard = styled(Panel)`
  padding: 28px;
  margin-top: 24px;
`;

const MetaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-end;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const MetaTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
`;

const PulseDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent);
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const ProcessingValue = styled.div`
  color: var(--accent);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.05em;
`;

const ProgressTrack = styled.div`
  margin-top: 20px;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
`;

const ProgressFill = styled.div`
  width: ${({ $value }) => `${$value}%`};
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(135deg, #bfdbfe 0%, #3b82f6 100%);
  box-shadow: 0 0 18px rgba(59, 130, 246, 0.36);
  transition: width 0.4s ease;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--line-soft);
`;

const StatLabel = styled.div`
  color: var(--subtle);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const StatValue = styled.div`
  margin-top: 8px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.04em;
`;

const SidebarStack = styled.div`
  display: grid;
  gap: 24px;
`;

const InfoCard = styled(Panel)`
  padding: 24px;
  position: relative;
  overflow: hidden;
`;

const InfoLead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ $tone = 'var(--blue)' }) => $tone};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const InfoTitle = styled.h3`
  margin: 14px 0 10px;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.04em;
`;

const InfoText = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.75;
`;

const SpecList = styled.div`
  display: grid;
  gap: 14px;
  margin-top: 6px;
`;

const SpecRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line-soft);

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const NeuralCard = styled(Panel)`
  position: relative;
  height: 100%;
  overflow: hidden;
`;

const NeuralImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.62;
  transition: all 0.6s ease;

  ${NeuralCard}:hover & {
    opacity: 0.9;
    transform: scale(1.05);
    filter: saturate(1.12);
  }
`;

const NeuralOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 24px;
  background: linear-gradient(to top, rgba(8, 17, 31, 0.94), transparent 65%);
`;

const MiniPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.14);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: #bfdbfe;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const Ping = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  animation: ${pulse} 1s ease-in-out infinite;
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ResultGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 48px 0;
  background: linear-gradient(
    to right,
    transparent,
    var(--line) 20%,
    var(--line) 80%,
    transparent
  );
`;

// Vertical Video Components (TikTok-style)
const VerticalVideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const VerticalVideoCard = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  }
`;

const VerticalVideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  background: #000;
  overflow: hidden;
`;

const VerticalVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  
  &::-webkit-media-controls {
    display: none !important;
  }
  
  &::-webkit-media-controls-enclosure {
    display: none !important;
  }
`;

const VerticalVideoPoster = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  transition: opacity 0.3s ease;
  opacity: ${({ $hidden }) => ($hidden ? '0' : '1')};
`;

const VerticalVideoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.3) 60%, transparent 100%);
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
  z-index: 2;
`;

const VerticalVideoInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const VerticalVideoTitle = styled.div`
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VerticalVideoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const VerticalMetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
`;

const VerticalPlayButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  z-index: 3;
  transition: all 0.3s ease;
  opacity: ${({ $playing }) => ($playing ? '0' : '1')};
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: rgba(0, 0, 0, 0.8);
    border-color: #fff;
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    margin-left: ${({ $playing }) => ($playing ? '0' : '3px')};
  }
`;

const VerticalLoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  z-index: 4;
  
  @keyframes spin {
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const VerticalDownloadButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  pointer-events: auto;
  z-index: 3;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    background: rgba(0, 0, 0, 0.8);
    border-color: #fff;
  }

  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const HighlightCard = styled(Panel)`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  grid-column: ${({ $featured }) => ($featured ? 'span 2' : 'span 1')};

  @media (max-width: 1080px) {
    grid-column: span 1;
  }
`;

const HighlightVisual = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: ${({ $featured }) => ($featured ? '21 / 9' : '16 / 9')};
`;

const HighlightImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ease;

  ${HighlightCard}:hover & {
    transform: scale(1.06);
  }
`;

const HighlightOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 18px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.82), transparent 58%);
  pointer-events: none;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $tone = 'rgba(59, 130, 246, 0.14)' }) => $tone};
  color: ${({ $color = '#ffffff' }) => $color};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const PlayButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  color: #ffffff;
  backdrop-filter: blur(10px);
  cursor: pointer;
  pointer-events: auto;
  
  svg {
    width: 20px;
    height: 20px;
    margin-left: 2px;
  }
`;

const InlineVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const InlineVideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: #000;
`;

const VideoLoadingBadge = styled.div`
  position: absolute;
  right: 18px;
  bottom: 18px;
  z-index: 5;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(8, 17, 31, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--text);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
`;

const HighlightContent = styled.div`
  padding: 22px;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
`;

const HighlightTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`;

const HighlightTitleBlock = styled.div`
  display: grid;
  gap: 6px;
`;

const HighlightHeadline = styled.h3`
  margin: 0;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: -0.04em;
`;

const HighlightMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $tone = 'rgba(255, 255, 255, 0.05)' }) => $tone};
  color: ${({ $color = 'var(--muted)' }) => $color};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const HighlightDescription = styled.p`
  margin: 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.8;
`;

const InsightSection = styled.div`
  display: grid;
  gap: 10px;
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--line-soft);
`;

const InsightLabel = styled.div`
  color: var(--accent);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const InsightText = styled.p`
  margin: 0;
  color: var(--text);
  font-size: 14px;
  line-height: 1.75;
`;

const LensList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2px;
`;

const LensPill = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(120, 168, 255, 0.1);
  border: 1px solid rgba(120, 168, 255, 0.14);
  color: #cfe0ff;
  font-size: 12px;
  font-weight: 700;
`;

const TruncatedText = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: ${({ $expanded }) => ($expanded ? 'unset' : '1')};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: ${({ $expanded }) => ($expanded ? '12px' : '0')};
  font-size: 12px;
  color: ${({ $type }) => ($type === 'transcript' ? 'var(--text)' : 'var(--muted)')};
  font-style: ${({ $type }) => ($type === 'transcript' ? 'italic' : 'normal')};
  line-height: ${({ $type }) => ($type === 'transcript' ? '1.6' : '1.8')};
  transition: all 0.2s ease;
`;

const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid var(--line-soft);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.04);
  color: var(--muted);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text);
  }
  
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    transition: transform 0.2s ease;
  }
`;

const TextRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15L12 9L6 15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5.14v13.72L19 12L8 5.14z" />
  </svg>
);

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
  flex-wrap: wrap;
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: ${({ $primary }) =>
    $primary ? 'linear-gradient(135deg, #bfdbfe 0%, #3b82f6 100%)' : 'rgba(255, 255, 255, 0.04)'};
  color: ${({ $primary }) => ($primary ? 'var(--accent-ink)' : 'var(--text)')};
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-top: 24px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled(Panel)`
  padding: 24px;
  grid-column: ${({ $full, $wide }) => ($full ? '1 / -1' : $wide ? 'span 2' : 'span 1')};

  @media (max-width: 560px) {
    grid-column: span 1;
  }
`;

const ConfidenceBar = styled.div`
  width: 100%;
  height: 10px;
  margin-top: 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
`;

const ConfidenceFill = styled.div`
  width: ${({ $value }) => `${$value}%`};
  height: 100%;
  background: linear-gradient(135deg, #bfdbfe 0%, #3b82f6 100%);
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(340px, 0.75fr);
  gap: 24px;

  @media (max-width: 1160px) {
    grid-template-columns: 1fr;
  }
`;

const TableCard = styled(Panel)`
  overflow: hidden;
`;

const HistoryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const THead = styled.thead`
  background: rgba(255, 255, 255, 0.03);
  color: var(--subtle);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
`;

const TH = styled.th`
  padding: 18px 24px;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
`;

const TR = styled.tr`
  background: ${({ $active }) => ($active ? 'rgba(120, 168, 255, 0.07)' : 'transparent')};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
`;

const TD = styled.td`
  padding: 20px 24px;
  border-top: 1px solid var(--line-soft);
  vertical-align: middle;
  color: ${({ $muted }) => ($muted ? 'var(--muted)' : 'var(--text)')};
  font-size: 14px;
`;

const VideoCell = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const ThumbWrap = styled.div`
  position: relative;
  width: 64px;
  height: 40px;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #000;
  flex-shrink: 0;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const QualityBadge = styled.span`
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.65);
  font-size: 8px;
  font-weight: 700;
`;

const VideoName = styled.div`
  font-weight: 700;
`;

const VideoMeta = styled.div`
  margin-top: 3px;
  color: var(--subtle);
  font-size: 11px;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ $status }) => ($status === 'processing' ? 'rgba(120, 168, 255, 0.12)' : 'rgba(59, 130, 246, 0.12)')};
  color: ${({ $status }) => ($status === 'processing' ? 'var(--blue)' : '#bfdbfe')};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  animation: ${({ $status }) => ($status === 'processing' ? pulse : 'none')} 1.2s ease-in-out infinite;
`;

const ActionWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 9px 12px;
  border: 1px solid ${({ $ghost, $danger }) => {
    if ($danger) return 'rgba(255, 110, 110, 0.22)';
    return $ghost ? 'transparent' : 'rgba(120, 168, 255, 0.16)';
  }};
  border-radius: 10px;
  background: ${({ $ghost, $danger }) => {
    if ($danger) return 'rgba(255, 110, 110, 0.12)';
    return $ghost ? 'transparent' : 'rgba(120, 168, 255, 0.1)';
  }};
  color: ${({ $ghost, $danger }) => {
    if ($danger) return '#ff9e9e';
    return $ghost ? 'var(--muted)' : 'var(--blue)';
  }};
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const Modal = styled.div`
  background: var(--panel-strong);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ModalIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 110, 110, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--danger);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text);
`;

const ModalBody = styled.div`
  margin-bottom: 24px;
`;

const CategorySelector = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
`;

const CategoryOption = styled.button`
  padding: 11px 16px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--accent)' : 'var(--line)')};
  border-radius: 999px;
  background: ${({ $active }) => ($active ? 'rgba(147, 197, 253, 0.14)' : 'rgba(255, 255, 255, 0.04)')};
  color: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--text)')};
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, color 0.2s;

  &:hover {
    border-color: var(--accent);
    background: rgba(147, 197, 253, 0.08);
  }
`;

const ScriptFileInput = styled.input`
  display: none;
`;

const ScriptFileLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px dashed ${({ $hasFile }) => ($hasFile ? 'var(--accent)' : 'var(--line)')};
  border-radius: 12px;
  background: ${({ $hasFile }) => ($hasFile ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)')};
  color: ${({ $hasFile }) => ($hasFile ? 'var(--text)' : 'var(--subtle)')};
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  word-break: break-all;

  &:hover {
    border-color: var(--accent);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const ModalText = styled.p`
  margin: 0 0 8px 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.6;
`;

const ModalFilename = styled.div`
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--line-soft);
  border-radius: 8px;
  color: var(--text);
  font-size: 13px;
  font-weight: 600;
  font-family: monospace;
  word-break: break-all;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 11px 20px;
  border: 1px solid ${({ $primary }) => ($primary ? 'rgba(255, 110, 110, 0.3)' : 'var(--line)')};
  border-radius: 10px;
  background: ${({ $primary }) => ($primary ? 'rgba(255, 110, 110, 0.18)' : 'rgba(255, 255, 255, 0.06)')};
  color: ${({ $primary }) => ($primary ? 'var(--danger)' : 'var(--text)')};
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $primary }) => ($primary ? 'rgba(255, 110, 110, 0.25)' : 'rgba(255, 255, 255, 0.1)')};
    border-color: ${({ $primary }) => ($primary ? 'rgba(255, 110, 110, 0.4)' : 'rgba(255, 255, 255, 0.15)')};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 18px 24px;
  border-top: 1px solid var(--line-soft);
  background: rgba(255, 255, 255, 0.02);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const FooterHint = styled.div`
  color: var(--subtle);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const Pager = styled.div`
  display: flex;
  gap: 8px;
`;

const PageButton = styled.button`
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'rgba(255, 255, 255, 0.05)')};
  color: ${({ $active }) => ($active ? 'var(--accent-ink)' : 'var(--text)')};
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
`;

const DetailCard = styled(Panel)`
  padding: 24px;
`;

const DetailHero = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 240px;
  border-radius: 22px;
  margin-bottom: 20px;
  background: #131313;
`;

const DetailImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
  opacity: 0.76;
`;

const DetailOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 22px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.84), transparent 62%);
`;

const DetailTitle = styled.h2`
  margin: 10px 0 6px;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.04em;
`;

const DetailText = styled.p`
  margin: 0;
  color: var(--muted);
  line-height: 1.75;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 20px 0;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const DetailBox = styled.div`
  padding: 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--line-soft);
`;

const InsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  margin-top: 28px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

function Dashboard() {
  const { user } = useAuthListener();
  const authUser = user || getAuthUser();
  const userEmail = authUser?.email || '';

  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('process');
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [uploadedName, setUploadedName] = useState('');
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [profile, setProfile] = useState({});

  // API State
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [currentJobResult, setCurrentJobResult] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [highlightImages, setHighlightImages] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [historyThumbnails, setHistoryThumbnails] = useState({});
  const [clipUrls, setClipUrls] = useState({});
  const [activeClipId, setActiveClipId] = useState(null);
  const [loadingClipId, setLoadingClipId] = useState(null);
  const [deletingJobId, setDeletingJobId] = useState(null);
  const [deleteConfirmJob, setDeleteConfirmJob] = useState(null);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [scriptFile, setScriptFile] = useState(null);
  const [selectedUploadCategory, setSelectedUploadCategory] = useState('phim');
  const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
  const [isSubmittingUpload, setIsSubmittingUpload] = useState(false);
  const [historyViewMode, setHistoryViewMode] = useState('list'); // 'list' or 'detail'
  const [activeVerticalVideoId, setActiveVerticalVideoId] = useState(null);
  const [verticalClipUrls, setVerticalClipUrls] = useState({});
  const [loadingVerticalClipId, setLoadingVerticalClipId] = useState(null);
  const [activeCenterVideoId, setActiveCenterVideoId] = useState(null);
  const [centerClipUrls, setCenterClipUrls] = useState({});
  const [loadingCenterClipId, setLoadingCenterClipId] = useState(null);
  const clipUrlsRef = useRef({});
  const verticalVideoRefs = useRef({});
  const centerVideoRefs = useRef({});

  // Restore profile from localStorage
  useEffect(() => {
    const selectedProfileId = localStorage.getItem('selectedProfileId');
    const storedUser = JSON.parse(localStorage.getItem('authUser'));
    if (selectedProfileId && storedUser?.profiles) {
      const savedProfile = storedUser.profiles.find((p) => p.id === selectedProfileId);
      if (savedProfile) {
        console.log('🔄 Restoring profile in dashboard:', savedProfile.name);
        setProfile(savedProfile);
      }
    }
  }, []);

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const data = await getJobHistory(userEmail);
      const items = data.items || [];
      setJobHistory(items);
      if (items.length > 0 && !selectedHistoryId) {
        setSelectedHistoryId(items[0].job_id);
      }
      return items;
    } catch (error) {
      console.error('Failed to load history:', error);
      return [];
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Load job history
  useEffect(() => {
    const loadHistory = async () => {
      if (activeTab !== 'history') return;

      await fetchHistory();
    };

    loadHistory();
  }, [activeTab, userEmail]);

  // Reset history view mode when switching tabs
  useEffect(() => {
    if (activeTab !== 'history') {
      setHistoryViewMode('list');
      // If switching to Studio from History detail view, reset to upload screen
      if (historyViewMode === 'detail' && activeTab === 'process') {
        handleReset();
      }
    }
  }, [activeTab, historyViewMode]);

  // Poll for job status when processing
  useEffect(() => {
    if (!currentJobId || phase !== 'processing') {
      return undefined;
    }

    const pollInterval = setInterval(async () => {
      try {
        const statusData = await getJobStatus(currentJobId, userEmail);

        if (statusData.progress) {
          setProgress(statusData.progress.percent || 0);
        }

        if (statusData.status === 'succeeded') {
          clearInterval(pollInterval);
          setPhase('completed');

          // Fetch the result
          const resultData = await getJobResult(currentJobId, userEmail);
          setCurrentJobResult(resultData);
          setProgress(100);
        } else if (statusData.status === 'failed') {
          clearInterval(pollInterval);
          setPhase('idle');
          setUploadError(statusData.error || 'Job failed');
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [currentJobId, phase, userEmail]);

  // Load highlight images when result is available
  useEffect(() => {
    const loadImages = async () => {
      if (!currentJobResult) {
        return;
      }

      const imageMap = {};

      const scenesToLoad = [
        ...(currentJobResult.metadata || []),
        ...((currentJobResult.highlights && currentJobResult.highlights.top_shots) || []),
      ];

      for (const scene of scenesToLoad) {
        if (scene.images && scene.images.length > 0) {
          try {
            const imageUrl = await getFileUrl(scene.images[0], userEmail);
            imageMap[scene.scene_id] = imageUrl;
          } catch (error) {
            console.error(`Failed to load image for scene ${scene.scene_id}:`, error);
          }
        }
      }

      setHighlightImages(imageMap);
    };

    loadImages();
  }, [currentJobResult, userEmail]);

  // Load thumbnails for history items
  useEffect(() => {
    const loadHistoryThumbnails = async () => {
      if (!jobHistory.length) return;

      const thumbnailMap = { ...historyThumbnails };

      for (const job of jobHistory) {
        // Only load for succeeded jobs that don't already have a thumbnail
        if (job.status === 'succeeded' && !thumbnailMap[job.job_id]) {
          try {
            const resultData = await getJobResult(job.job_id, userEmail);
            if (resultData.metadata && resultData.metadata.length > 0) {
              const firstScene = resultData.metadata[0];
              if (firstScene.images && firstScene.images.length > 0) {
                const imageUrl = await getFileUrl(firstScene.images[0], userEmail);
                thumbnailMap[job.job_id] = imageUrl;
              }
            }
          } catch (error) {
            console.error(`Failed to load thumbnail for job ${job.job_id}:`, error);
          }
        }
      }

      setHistoryThumbnails(thumbnailMap);
    };

    loadHistoryThumbnails();
  }, [jobHistory, userEmail]);

  useEffect(() => {
    clipUrlsRef.current = clipUrls;
  }, [clipUrls]);

  useEffect(() => () => {
    Object.values(clipUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });
  }, []);

  // Cleanup vertical video URLs on unmount
  useEffect(() => () => {
    Object.values(verticalClipUrls).forEach((url) => {
      URL.revokeObjectURL(url);
    });
  }, [verticalClipUrls]);

  // Cleanup center video URLs on unmount
  useEffect(() => () => {
    Object.values(centerClipUrls).forEach((url) => {
      URL.revokeObjectURL(url);
    });
  }, [centerClipUrls]);

  const filteredHistory = useMemo(() => {
    const keyword = searchQuery.trim().toLowerCase();
    const historyItems = keyword
      ? jobHistory.filter((item) =>
        [item.input_path, item.status, item.job_id].some((value) =>
          value?.toLowerCase().includes(keyword)
        )
      )
      : jobHistory;

    return [...historyItems].sort((a, b) => {
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;

      return timeB - timeA;
    });
  }, [searchQuery, jobHistory]);

  const selectedHistory = useMemo(
    () => filteredHistory.find((item) => item.job_id === selectedHistoryId) || filteredHistory[0],
    [filteredHistory, selectedHistoryId]
  );

  useEffect(() => {
    if (!filteredHistory.length) {
      return;
    }

    const isSelectedVisible = filteredHistory.some((item) => item.job_id === selectedHistoryId);

    if (!isSelectedVisible) {
      setSelectedHistoryId(filteredHistory[0].job_id);
    }
  }, [filteredHistory, selectedHistoryId]);

  const handleUploadClick = () => {
    const hasProcessingJob =
      phase === 'processing' ||
      jobHistory.some((job) => job.status !== 'succeeded' && job.status !== 'failed');

    if (hasProcessingJob) {
      setUploadError('Vui lòng chờ tất cả các job hiện tại hoàn thành trước khi tải video mới.');
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const clearClipPreviewCache = () => {
    Object.values(clipUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    Object.values(verticalClipUrls).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    clipUrlsRef.current = {};
    setClipUrls({});
    setActiveClipId(null);
    setLoadingClipId(null);
    setVerticalClipUrls({});
    setActiveVerticalVideoId(null);
    setLoadingVerticalClipId(null);
    verticalVideoRefs.current = {};
    setCenterClipUrls({});
    setActiveCenterVideoId(null);
    setLoadingCenterClipId(null);
    centerVideoRefs.current = {};
  };

  const clearPendingUpload = () => {
    setPendingUploadFile(null);
    setScriptFile(null);
    setSelectedUploadCategory('phim');
    setIsScriptModalOpen(false);
  };

  const submitPendingUpload = async () => {
    if (!pendingUploadFile) {
      return;
    }

    setProgress(0);
    setPhase('processing');
    setActiveTab('process');
    setUploadError(null);
    setCurrentJobId(null);
    setCurrentJobResult(null);
    clearClipPreviewCache();
    setIsSubmittingUpload(true);

    try {
      const normalizedScriptFile = selectedUploadCategory === 'phim' ? scriptFile : null;
      const response = await uploadVideo(
        pendingUploadFile,
        userEmail,
        selectedUploadCategory,
        normalizedScriptFile
      );
      setCurrentJobId(response.job_id);
      setUploadedName(pendingUploadFile.name);
      clearPendingUpload();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error.message || 'Upload failed');
      setPhase('idle');
    } finally {
      setIsSubmittingUpload(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    if (file.type !== 'video/mp4') {
      setUploadError('Chỉ hỗ trợ định dạng MP4. Vui lòng chọn file .mp4.');

      if (event.target) {
        event.target.value = '';
      }

      return;
    }

    if (file.size >= MAX_UPLOAD_SIZE_BYTES) {
      setUploadError('Chỉ hỗ trợ file nhỏ hơn 1GB.');

      if (event.target) {
        event.target.value = '';
      }

      return;
    }

    // Check video duration (max 60 minutes)
    try {
      const duration = await new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        const objectUrl = URL.createObjectURL(file);
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(video.duration);
        };
        video.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error('Không thể đọc thông tin video.'));
        };
        video.src = objectUrl;
      });

      if (duration > 60 * 60 + 1) {
        setUploadError('Chỉ hỗ trợ video có thời lượng dưới 60 phút.');

        if (event.target) {
          event.target.value = '';
        }

        return;
      }
    } catch (err) {
      // If we can't read metadata, let the server validate
      console.warn('Could not check video duration:', err.message);
    }

    setActiveTab('process');
    setUploadError(null);
    setCurrentJobResult(null);
    clearClipPreviewCache();

    setUploadedName(file.name);
    setPendingUploadFile(file);
    setScriptFile(null);
    setSelectedUploadCategory('phim');
    setIsScriptModalOpen(true);

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleSkipScript = async () => {
    await submitPendingUpload();
  };

  const handleScriptSubmit = async (event) => {
    event.preventDefault();
    await submitPendingUpload();
  };

  const handleReset = () => {
    setPhase('idle');
    setProgress(0);
    setUploadedName('');
    setCurrentJobId(null);
    setCurrentJobResult(null);
    setUploadError(null);
    clearPendingUpload();
    clearClipPreviewCache();
  };

  const handleDashboardRefresh = () => {
    window.location.reload();
  };

  const handleLogout = () => {
    clearAuthData();
    window.location.href = ROUTES.SIGN_IN;
  };

  const handleOpenHistoryResult = async (item) => {
    setSelectedHistoryId(item.job_id);

    if (item.status !== 'succeeded') {
      return;
    }

    try {
      const resultData = await getJobResult(item.job_id, userEmail);
      setCurrentJobResult(resultData);
      setCurrentJobId(item.job_id);
      setPhase('completed');
      // Không tự động chuyển tab - để user xem preview trong History
    } catch (error) {
      console.error('Failed to load result:', error);
    }
  };

  const handleSelectHistoryItem = (item) => {
    // Chỉ select item, không load data
    setSelectedHistoryId(item.job_id);
  };

  const handleViewInStudio = async (item) => {
    // Load data và chuyển sang detail view trong tab History
    await handleOpenHistoryResult(item);
    setHistoryViewMode('detail');
  };

  const handleBackToList = () => {
    setHistoryViewMode('list');
    setCurrentJobResult(null);
    setCurrentJobId(null);
  };

  const handleViewInStudioTab = async (item) => {
    // Load data và chuyển sang tab Studio để xem full detail
    await handleOpenHistoryResult(item);
    setActiveTab('process');
  };

  const handleDeleteJob = async (item) => {
    setDeleteConfirmJob(item);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmJob) return;

    const item = deleteConfirmJob;
    setDeleteConfirmJob(null);
    setDeletingJobId(item.job_id);

    try {
      await deleteJob(item.job_id, userEmail);

      setJobHistory((prev) => prev.filter((job) => job.job_id !== item.job_id));
      setHistoryThumbnails((prev) => {
        const next = { ...prev };
        delete next[item.job_id];
        return next;
      });

      if (selectedHistoryId === item.job_id) {
        setSelectedHistoryId(null);
      }

      if (currentJobId === item.job_id) {
        handleReset();
      }

      await fetchHistory();
    } catch (error) {
      console.error('Failed to delete job:', error);
      alert('Không thể xóa job. Vui lòng thử lại.');
    } finally {
      setDeletingJobId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmJob(null);
  };

  const toggleExpand = (itemId, field) => {
    const key = `${itemId}-${field}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatTimecode = (timecode) => {
    if (!timecode) return '';
    // Remove milliseconds: "00:00:04.126" -> "00:00:04"
    const withoutMs = timecode.split('.')[0];
    const parts = withoutMs.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);

    // If hours is 0, return mm:ss format
    if (hours === 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    // Otherwise return hh:mm:ss format
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    if (typeof seconds !== 'number' || Number.isNaN(seconds)) {
      return '--';
    }

    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFilename = (path) => {
    if (!path) return 'Unknown';
    const parts = path.split('/');
    return parts[parts.length - 1];
  };

  const getConfidenceValue = (confidence) => {
    const lookup = {
      high: 92,
      medium: 74,
      low: 52,
    };

    return lookup[(confidence || '').toLowerCase()] || 0;
  };

  const getConfidenceTone = (confidence) => {
    const normalized = (confidence || '').toLowerCase();

    if (normalized === 'high') {
      return {
        bg: 'rgba(59, 130, 246, 0.16)',
        color: '#bfdbfe',
      };
    }

    if (normalized === 'medium') {
      return {
        bg: 'rgba(255, 217, 142, 0.16)',
        color: '#ffe3a9',
      };
    }

    return {
      bg: 'rgba(120, 168, 255, 0.12)',
      color: '#cfe0ff',
    };
  };

  const handlePlayInline = async (item) => {
    if (!item.clipPath) {
      return;
    }

    if (activeClipId === item.previewId) {
      setActiveClipId(null);
      return;
    }

    if (clipUrls[item.previewId]) {
      setActiveClipId(item.previewId);
      return;
    }

    try {
      setLoadingClipId(item.previewId);
      const clipUrl = await getFileUrl(item.clipPath, userEmail);
      setClipUrls((prev) => ({
        ...prev,
        [item.previewId]: clipUrl,
      }));
      setActiveClipId(item.previewId);
    } catch (error) {
      console.error('Failed to load clip:', error);
    } finally {
      setLoadingClipId(null);
    }
  };

  const handleVerticalVideoPlay = async (item) => {
    if (!item.clipSocialPath) {
      return;
    }

    const verticalId = `vertical-${item.previewId}`;

    // If clicking the currently playing video, pause it
    if (activeVerticalVideoId === verticalId) {
      const videoElement = verticalVideoRefs.current[verticalId];
      if (videoElement) {
        videoElement.pause();
        setActiveVerticalVideoId(null);
      }
      return;
    }

    // Pause any currently playing video
    if (activeVerticalVideoId) {
      const currentVideo = verticalVideoRefs.current[activeVerticalVideoId];
      if (currentVideo) {
        currentVideo.pause();
      }
    }

    // If URL already loaded, just play
    if (verticalClipUrls[verticalId]) {
      setActiveVerticalVideoId(verticalId);
      setTimeout(() => {
        const videoElement = verticalVideoRefs.current[verticalId];
        if (videoElement) {
          videoElement.play().catch(console.error);
        }
      }, 100);
      return;
    }

    // Load new clip
    try {
      setLoadingVerticalClipId(verticalId);
      const clipUrl = await getFileUrl(item.clipSocialPath, userEmail);
      setVerticalClipUrls((prev) => ({
        ...prev,
        [verticalId]: clipUrl,
      }));
      setActiveVerticalVideoId(verticalId);
      // Play will happen automatically after video loads via onLoadedData
    } catch (error) {
      console.error('Failed to load vertical clip:', error);
    } finally {
      setLoadingVerticalClipId(null);
    }
  };

  const handleDownloadVerticalVideo = async (item) => {
    if (!item.clipSocialPath) {
      return;
    }

    try {
      // Get the file URL
      const fileUrl = await getFileUrl(item.clipSocialPath, userEmail);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${item.headline || 'video'}_social.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Note: The blob URL will be automatically revoked by our cleanup effects
    } catch (error) {
      console.error('Failed to download vertical video:', error);
      alert('Không thể tải video. Vui lòng thử lại.');
    }
  };

  const handleCenterVideoPlay = async (item) => {
    if (!item.clipSocialCenterPath) {
      return;
    }

    const centerId = `center-${item.previewId}`;

    if (activeCenterVideoId === centerId) {
      const videoElement = centerVideoRefs.current[centerId];
      if (videoElement) {
        videoElement.pause();
        setActiveCenterVideoId(null);
      }
      return;
    }

    if (activeCenterVideoId) {
      const currentVideo = centerVideoRefs.current[activeCenterVideoId];
      if (currentVideo) {
        currentVideo.pause();
      }
    }

    if (centerClipUrls[centerId]) {
      setActiveCenterVideoId(centerId);
      setTimeout(() => {
        const videoElement = centerVideoRefs.current[centerId];
        if (videoElement) {
          videoElement.play().catch(console.error);
        }
      }, 100);
      return;
    }

    try {
      setLoadingCenterClipId(centerId);
      const clipUrl = await getFileUrl(item.clipSocialCenterPath, userEmail);
      setCenterClipUrls((prev) => ({
        ...prev,
        [centerId]: clipUrl,
      }));
      setActiveCenterVideoId(centerId);
    } catch (error) {
      console.error('Failed to load center clip:', error);
    } finally {
      setLoadingCenterClipId(null);
    }
  };

  const handleDownloadCenterVideo = async (item) => {
    if (!item.clipSocialCenterPath) {
      return;
    }

    try {
      const fileUrl = await getFileUrl(item.clipSocialCenterPath, userEmail);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${item.headline || 'video'}_social_center.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download center video:', error);
      alert('Không thể tải video. Vui lòng thử lại.');
    }
  };

  const renderProcessContent = () => {
    const isAwaitingCategorySelection = isScriptModalOpen && pendingUploadFile;
    const needsScriptFile = selectedUploadCategory === 'phim';

    if (phase === 'completed' && currentJobResult && currentJobResult.metadata) {
      const highlightSummary = currentJobResult.highlights;
      const aiHighlights = (highlightSummary && highlightSummary.top_shots) || [];
      const hasAiHighlights = aiHighlights.length > 0;
      const highlightLimit = highlightSummary && highlightSummary.top_k ? highlightSummary.top_k : aiHighlights.length;
      const aiHighlightItems = hasAiHighlights
        ? aiHighlights.slice(0, highlightLimit || aiHighlights.length).map((scene, index) => {
          return {
            id: scene.scene_id,
            previewId: `highlight-${scene.scene_id}`,
            rank: scene.rank || index + 1,
            score: scene.highlight_score,
            title: `Time: ${formatTimecode(scene.start_timecode)} - ${formatTimecode(scene.end_timecode)}`,
            headline: scene.headline,
            description: scene.description || scene.transcript || 'No description available',
            duration: formatDuration(scene.duration_seconds),
            transcript: scene.transcript,
            image: highlightImages[scene.scene_id] || null,
            clipPath: scene.clip_path,
            clipSocialPath: scene.clip_social_path,
            clipSocialCenterPath: scene.clip_social__center_path,
            confidence: scene.confidence,
            whyItStandsOut: scene.why_it_stands_out,
            editorialUse: scene.editorial_use,
            transcriptSignal: scene.transcript_signal,
            visualSignal: scene.visual_signal,
            featured: index === 0,
          };
        })
        : [];

      const metadataItems = (currentJobResult.metadata || []).map((scene, index) => {
        return {
          id: scene.scene_id,
          previewId: `metadata-${scene.scene_id}`,
          title: `Scene ${scene.scene_index}: ${formatTimecode(scene.start_timecode)} - ${formatTimecode(scene.end_timecode)}`,
          headline: `Scene ${scene.scene_index}`,
          description: scene.description || scene.transcript || 'No description available',
          duration: formatDuration(scene.duration_seconds),
          transcript: scene.transcript,
          image: highlightImages[scene.scene_id] || null,
          clipPath: scene.clip_path,
          featured: !hasAiHighlights && index === 0,
        };
      });

      return (
        <>
          <ResultHeader>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
                <GhostButton onClick={handleReset}>
                  + &nbsp; Upload video mới
                </GhostButton>
              </div>
              <Title>{uploadedName || 'Video đã xử lý'}</Title>
              {/* <Subtitle>
                {hasAiHighlights
                  ? `AI đã chọn ra ${aiHighlightItems.length} highlight nổi bật nhất.`
                  : `Video của bạn đã được phân tích thành ${aiHighlightItems.length} cảnh.`}
              </Subtitle> */}
            </div>
          </ResultHeader>



          {hasAiHighlights && (
            <ResultGrid>
              {aiHighlightItems.map((item, index) => (
                <HighlightCard key={item.id} $featured={index === 0}>
                  <HighlightVisual $featured={index === 0}>
                    {activeClipId === item.previewId && clipUrls[item.previewId] && (
                      <InlineVideoOverlay>
                        <InlineVideo
                          autoPlay
                          controls
                          playsInline
                          poster={item.image || undefined}
                          src={clipUrls[item.previewId]}
                        />
                      </InlineVideoOverlay>
                    )}
                    {item.image ? (
                      <HighlightImage alt={item.title} src={item.image} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      </div>
                    )}
                    {loadingClipId === item.previewId && (
                      <VideoLoadingBadge>Dang tai clip...</VideoLoadingBadge>
                    )}
                    <HighlightOverlay>
                      <div>
                        <HighlightMeta>
                          {index === 0 && <Tag>Nổi bật nhất</Tag>}
                          {item.rank ? <Tag $tone="rgba(120, 168, 255, 0.16)" $color="#d6e5ff">Top {item.rank}</Tag> : null}
                        </HighlightMeta>
                      </div>
                      {activeClipId !== item.previewId && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 12,
                          }}
                        >
                          <PlayButton type="button" onClick={() => handlePlayInline(item)}>
                            <PlayIcon />
                          </PlayButton>
                          <div>
                            <div style={{ fontWeight: 800 }}>{item.title}</div>
                            <div
                              style={{
                                fontSize: 12,
                                color: 'rgba(255,255,255,0.68)',
                              }}
                            >
                              Thời lượng: {item.duration}
                            </div>
                          </div>
                        </div>
                      )}
                    </HighlightOverlay>
                  </HighlightVisual>
                  <HighlightContent>
                    <HighlightTopRow>
                      <HighlightTitleBlock>
                        <HighlightHeadline>{item.headline || item.title}</HighlightHeadline>
                        {/* <HighlightMeta>
                          <MetaPill>{item.duration}</MetaPill>
                          {typeof item.score === 'number' ? (
                            <MetaPill $tone="rgba(59, 130, 246, 0.14)" $color="#bfdbfe">
                              Score {item.score}
                            </MetaPill>
                          ) : null}
                          {item.confidence ? (
                            <MetaPill
                              $tone={getConfidenceTone(item.confidence).bg}
                              $color={getConfidenceTone(item.confidence).color}
                            >
                              {item.confidence}
                            </MetaPill>
                          ) : null}
                        </HighlightMeta> */}
                      </HighlightTitleBlock>
                    </HighlightTopRow>

                    {/* {item.confidence ? (
                      <div>
                        <StatLabel>Confidence</StatLabel>
                        <ConfidenceBar>
                          <ConfidenceFill $value={getConfidenceValue(item.confidence)} />
                        </ConfidenceBar>
                      </div>
                    ) : null} */}

                    {item.whyItStandsOut && (
                      <InsightSection>
                        <InsightLabel>Vì sao AI chọn đoạn này</InsightLabel>
                        <InsightText>{item.whyItStandsOut}</InsightText>
                      </InsightSection>
                    )}

                    {item.editorialUse && (
                      <InsightSection>
                        <InsightLabel>Gợi ý sử dụng</InsightLabel>
                        <InsightText>{item.editorialUse}</InsightText>
                      </InsightSection>
                    )}


                  </HighlightContent>
                </HighlightCard>
              ))}
            </ResultGrid>
          )}

          <Divider />

          <ResultHeader>
            <TitleSocial>Format Dọc Social</TitleSocial>
          </ResultHeader>

          <VerticalVideoGrid>
            {aiHighlightItems.map((item, index) => {
              const verticalId = `vertical-${item.previewId}`;
              const isPlaying = activeVerticalVideoId === verticalId;
              const isLoading = loadingVerticalClipId === verticalId;

              return (
                <VerticalVideoCard key={verticalId}>
                  <VerticalVideoContainer>
                    {item.image && (
                      <VerticalVideoPoster
                        src={item.image}
                        alt={item.title}
                        $hidden={isPlaying && verticalClipUrls[verticalId]}
                      />
                    )}

                    {verticalClipUrls[verticalId] && (
                      <VerticalVideo
                        ref={(el) => { verticalVideoRefs.current[verticalId] = el; }}
                        src={verticalClipUrls[verticalId]}
                        playsInline
                        loop
                        onLoadedData={(e) => {
                          if (activeVerticalVideoId === verticalId) {
                            e.target.play().catch(console.error);
                          }
                        }}
                        onEnded={() => {
                          setActiveVerticalVideoId(null);
                        }}
                        onClick={() => {
                          const video = verticalVideoRefs.current[verticalId];
                          if (video) {
                            if (video.paused) {
                              video.play();
                              setActiveVerticalVideoId(verticalId);
                            } else {
                              video.pause();
                              setActiveVerticalVideoId(null);
                            }
                          }
                        }}
                      />
                    )}

                    {isLoading && <VerticalLoadingSpinner />}

                    <VerticalDownloadButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadVerticalVideo(item);
                      }}
                      title="Tải xuống video"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                      </svg>
                    </VerticalDownloadButton>

                    <VerticalPlayButton
                      $playing={isPlaying}
                      onClick={() => handleVerticalVideoPlay(item)}
                    >
                      {isPlaying ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <PlayIcon />
                      )}
                    </VerticalPlayButton>

                    <VerticalVideoOverlay>
                      <VerticalVideoInfo>
                        <VerticalVideoTitle>{item.headline || item.title}</VerticalVideoTitle>
                        <VerticalVideoMeta>
                          <VerticalMetaBadge>{item.duration}</VerticalMetaBadge>
                        </VerticalVideoMeta>
                      </VerticalVideoInfo>
                    </VerticalVideoOverlay>
                  </VerticalVideoContainer>
                </VerticalVideoCard>
              );
            })}
          </VerticalVideoGrid>

          <Divider />

          <ResultHeader>
            <TitleSocial>Format Dọc Social Center</TitleSocial>
          </ResultHeader>

          <VerticalVideoGrid>
            {aiHighlightItems.map((item, index) => {
              const centerId = `center-${item.previewId}`;
              const isPlaying = activeCenterVideoId === centerId;
              const isLoading = loadingCenterClipId === centerId;

              return (
                <VerticalVideoCard key={centerId}>
                  <VerticalVideoContainer>
                    {item.image && (
                      <VerticalVideoPoster
                        src={item.image}
                        alt={item.title}
                        $hidden={isPlaying && centerClipUrls[centerId]}
                      />
                    )}

                    {centerClipUrls[centerId] && (
                      <VerticalVideo
                        ref={(el) => { centerVideoRefs.current[centerId] = el; }}
                        src={centerClipUrls[centerId]}
                        playsInline
                        loop
                        onLoadedData={(e) => {
                          if (activeCenterVideoId === centerId) {
                            e.target.play().catch(console.error);
                          }
                        }}
                        onEnded={() => {
                          setActiveCenterVideoId(null);
                        }}
                        onClick={() => {
                          const video = centerVideoRefs.current[centerId];
                          if (video) {
                            if (video.paused) {
                              video.play();
                              setActiveCenterVideoId(centerId);
                            } else {
                              video.pause();
                              setActiveCenterVideoId(null);
                            }
                          }
                        }}
                      />
                    )}

                    {isLoading && <VerticalLoadingSpinner />}

                    <VerticalDownloadButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadCenterVideo(item);
                      }}
                      title="Tải xuống video"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                      </svg>
                    </VerticalDownloadButton>

                    <VerticalPlayButton
                      $playing={isPlaying}
                      onClick={() => handleCenterVideoPlay(item)}
                    >
                      {isPlaying ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <PlayIcon />
                      )}
                    </VerticalPlayButton>

                    <VerticalVideoOverlay>
                      <VerticalVideoInfo>
                        <VerticalVideoTitle>{item.headline || item.title}</VerticalVideoTitle>
                        <VerticalVideoMeta>
                          <VerticalMetaBadge>{item.duration}</VerticalMetaBadge>
                        </VerticalVideoMeta>
                      </VerticalVideoInfo>
                    </VerticalVideoOverlay>
                  </VerticalVideoContainer>
                </VerticalVideoCard>
              );
            })}
          </VerticalVideoGrid>

          <Divider />

          <ResultHeader>
            <div>
              <TitleSocial>Tất cả các scene của video</TitleSocial>
            </div>
          </ResultHeader>

          <ResultGrid>
            {metadataItems.map((item, index) => (
              <HighlightCard key={item.id} $featured={index === 0}>
                <HighlightVisual $featured={index === 0}>
                  {activeClipId === item.previewId && clipUrls[item.previewId] && (
                    <InlineVideoOverlay>
                      <InlineVideo
                        autoPlay
                        controls
                        playsInline
                        poster={item.image || undefined}
                        src={clipUrls[item.previewId]}
                      />
                    </InlineVideoOverlay>
                  )}
                  {item.image ? (
                    <HighlightImage alt={item.title} src={item.image} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    </div>
                  )}
                  {loadingClipId === item.previewId && (
                    <VideoLoadingBadge>Đang tải clip...</VideoLoadingBadge>
                  )}
                  <HighlightOverlay>
                    <div>
                      <HighlightMeta>
                        <Tag $tone="rgba(255, 255, 255, 0.12)">Scene</Tag>
                      </HighlightMeta>
                    </div>
                    {activeClipId !== item.previewId && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 12,
                        }}
                      >
                        {item.clipPath && (
                          <PlayButton type="button" onClick={() => handlePlayInline(item)}>
                            <PlayIcon />
                          </PlayButton>
                        )}
                        <div>
                          <div style={{ fontWeight: 800 }}>{item.title}</div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'rgba(255,255,255,0.68)',
                            }}
                          >
                            {item.duration}
                          </div>
                        </div>
                      </div>
                    )}
                  </HighlightOverlay>
                </HighlightVisual>
                <HighlightContent>
                  <HighlightTopRow>
                    <HighlightTitleBlock>
                      <HighlightHeadline>{item.headline || item.title}</HighlightHeadline>
                      <HighlightMeta>
                        <MetaPill>{item.duration}</MetaPill>
                      </HighlightMeta>
                    </HighlightTitleBlock>
                  </HighlightTopRow>

                  {/* {item.transcript && (
                    <div>
                      <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Transcript
                      </div>
                      <TextRow>
                        <TruncatedText $expanded={expandedItems[`${item.id}-transcript`]} $type="transcript">
                          "{item.transcript}"
                        </TruncatedText>
                        <ExpandButton onClick={() => toggleExpand(item.id, 'transcript')}>
                          {expandedItems[`${item.id}-transcript`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </ExpandButton>
                      </TextRow>
                    </div>
                  )} */}
                  {item.description && (
                    <div style={{ marginTop: item.transcript ? 12 : 0 }}>
                      <TextRow>
                        <TruncatedText $expanded={expandedItems[`${item.id}-description`]} $type="description">
                          {item.description}
                        </TruncatedText>
                        <ExpandButton onClick={() => toggleExpand(item.id, 'description')}>
                          {expandedItems[`${item.id}-description`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </ExpandButton>
                      </TextRow>
                    </div>
                  )}
                </HighlightContent>
              </HighlightCard>
            ))}
          </ResultGrid>

        </>
      );
    }

    return (
      <>
        <HeroSection>
          <Eyebrow>LUMINATION AI</Eyebrow>
          <Title>Biến video dài thành shorts ấn tượng</Title>
        </HeroSection>

        <UploadGrid>
          <div>
            <UploadCanvas>
              <CanvasGlow />
              <UploadInner>
                <UploadHeading>Tải video lên để trích xuất điểm nổi bật</UploadHeading>
                <UploadText>
                  Hỗ trợ định dạng MP4. Hệ thống tự động phân tích chuyển cảnh, chuyển động, nội dung thoại và phản ứng để tìm ra những khoảnh khắc đáng chú ý nhất trong video của bạn.
                </UploadText>
                <PrimaryButton onClick={handleUploadClick}>
                  Chọn file video
                </PrimaryButton>
                {uploadError && (
                  <div style={{ marginTop: 18, color: 'var(--danger)', fontSize: 13 }}>
                    Lỗi: {uploadError}
                  </div>
                )}
                <UploadHelper>
                  {pendingUploadFile
                    ? `Tệp đã chọn: ${pendingUploadFile.name}`
                    : uploadedName
                      ? `Tệp đã tải: ${uploadedName}`
                      : 'Thoả mãn 4 điều kiện bên dưới'}
                </UploadHelper>
                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.07)', borderRadius: 4, padding: '3px 8px' }}>
                    📹 MP4
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.07)', borderRadius: 4, padding: '3px 8px' }}>
                    ⏱ Tối đa 60 phút
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.07)', borderRadius: 4, padding: '3px 8px' }}>
                    💾 Tối đa 1GB
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', background: 'rgba(255,255,255,0.07)', borderRadius: 4, padding: '3px 8px' }}>
                    🚫 1 job tại một thời điểm
                  </span>
                </div>
              </UploadInner>
            </UploadCanvas>

            <ProcessingCard>
              <MetaHeader>
                <div>
                  <MetaTitle>
                    {phase === 'processing' ? (
                      <>
                        <PulseDot />
                        Đang trích xuất các điểm nổi bật
                      </>
                    ) : isAwaitingCategorySelection ? (
                      <>
                        Đang chờ chọn category
                      </>
                    ) : (
                      <>
                        Sẵn sàng phân tích video mới
                      </>
                    )}
                  </MetaTitle>
                  <InfoText style={{ marginTop: 8 }}>
                    {phase === 'processing'
                      ? `Đang xử lý ${uploadedName}`
                      : isAwaitingCategorySelection
                        ? needsScriptFile
                          ? `Chọn category cho ${pendingUploadFile.name}. Nếu là phim, bạn có thể đính kèm thêm file kịch bản trước khi xử lý.`
                          : `Chọn category cho ${pendingUploadFile.name} rồi submit để xử lý ngay.`
                        : 'Tải video lên để bắt đầu quá trình phân tích tự động'}
                  </InfoText>
                </div>
                <ProcessingValue>{phase === 'processing' ? `${Math.round(progress)}%` : '0'}</ProcessingValue>
              </MetaHeader>

              <ProgressTrack>
                <ProgressFill $value={phase === 'processing' ? progress : 0} />
              </ProgressTrack>

              <StatsRow>
                <StatBox>
                  <StatLabel>Trạng Thái</StatLabel>
                  <StatValue style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {phase === 'processing' ? 'Đang xử lý' : isAwaitingCategorySelection ? 'Chờ category' : 'Chờ'}
                  </StatValue>
                </StatBox>
                <StatBox>
                  <StatLabel>Tiến Độ</StatLabel>
                  <StatValue style={{ fontSize: 12, wordBreak: 'break-all' }}>{phase === 'processing' ? `${Math.round(progress)}%` : '--'}</StatValue>
                </StatBox>
                <StatBox>
                  <StatLabel>Mã Công Việc</StatLabel>
                  <StatValue style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {currentJobId ? currentJobId.substring(0, 8) + '...' : isAwaitingCategorySelection ? 'Chưa tạo' : 'Chờ'}
                  </StatValue>
                </StatBox>
              </StatsRow>
            </ProcessingCard>
          </div>

          <SidebarStack>

            <NeuralCard>
              <NeuralImage
                alt="Neural analysis"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuCGNmNVubqy19R6xS9KNfkpcuhD1HVuaiKgeLGy9eP5P_zUPRdd35iw-d9j8baeNR7hTXJtM0JFlpQwTLUXAMgYG4PedcWxyD-CvcCS3mUaalwYCChJCnb-uHJ_f09sOVtMPYZt-tea2SbTDprlleZUx590Ij48fo9Gvv7pO9eP3wF-ejcfMWZm43lM3QwIrPHPEXn35h_N8h0KLlTCc2jopKQBpoOeTXYj5sW0DgcgznyGiq22IqqXg1h96nzN02AWFl3E2AasGfyP"
              />
              <NeuralOverlay>
                <MiniPill>
                  <Ping />
                  Bản đồ tín hiệu trực tiếp
                </MiniPill>
                <InfoText style={{ marginTop: 12 }}>
                  Hiển thị quá trình chấm điểm khi hệ thống so sánh đỉnh chuyển động, cắt cảnh và điểm nhấn trong lời thoại
                </InfoText>
              </NeuralOverlay>
            </NeuralCard>
          </SidebarStack>
        </UploadGrid>
      </>
    );
  };

  const renderHistoryContent = () => {
    // Helper to format date
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${hours}:${minutes} - ${day}/${month}/${year}`;
    };

    // Helper to format duration
    const formatDuration = (seconds) => {
      if (!seconds) return 'N/A';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Helper to calculate total video duration from metadata
    const getTotalDuration = (job) => {
      if (job.metadata_count && job.progress?.total_scenes) {
        // Estimate based on metadata count
        return 'Processing';
      }
      return 'N/A';
    };

    // If in detail view mode, show detail view
    if (historyViewMode === 'detail' && currentJobResult && currentJobId) {
      const highlightSummary = currentJobResult.highlights;
      const aiHighlights = (highlightSummary && highlightSummary.top_shots) || [];
      const hasAiHighlights = aiHighlights.length > 0;
      const highlightLimit = highlightSummary && highlightSummary.top_k ? highlightSummary.top_k : aiHighlights.length;
      const aiHighlightItems = hasAiHighlights
        ? aiHighlights.slice(0, highlightLimit || aiHighlights.length).map((scene, index) => ({
          id: scene.scene_id,
          previewId: `highlight-${scene.scene_id}`,
          rank: scene.rank || index + 1,
          score: scene.highlight_score,
          title: `Time: ${formatTimecode(scene.start_timecode)} - ${formatTimecode(scene.end_timecode)}`,
          headline: scene.headline,
          description: scene.description || scene.transcript || 'No description available',
          duration: formatDuration(scene.duration_seconds),
          image: highlightImages[scene.scene_id] || null,
          clipPath: scene.clip_path,
          clipSocialPath: scene.clip_social_path,
          clipSocialCenterPath: scene.clip_social__center_path,
          confidence: scene.confidence,
          whyItStandsOut: scene.why_it_stands_out,
          editorialUse: scene.editorial_use,
          featured: index === 0,
        }))
        : (currentJobResult.metadata || []).map((scene, index) => ({
          id: scene.scene_id,
          previewId: `metadata-${scene.scene_id}`,
          title: `Scene ${scene.scene_index}`,
          headline: `Scene ${scene.scene_index}`,
          description: scene.description || scene.transcript || 'No description available',
          duration: formatDuration(scene.duration_seconds),
          image: highlightImages[scene.scene_id] || null,
          clipPath: scene.clip_path,
          featured: index === 0,
        }));

      const metadataItems = (currentJobResult.metadata || []).map((scene, index) => ({
        id: scene.scene_id,
        previewId: `metadata-${scene.scene_id}`,
        title: `Scene ${scene.scene_index}: ${formatTimecode(scene.start_timecode)} - ${formatTimecode(scene.end_timecode)}`,
        headline: `Scene ${scene.scene_index}`,
        description: scene.description || scene.transcript || 'No description available',
        duration: formatDuration(scene.duration_seconds),
        transcript: scene.transcript,
        image: highlightImages[scene.scene_id] || null,
        clipPath: scene.clip_path,
        featured: !hasAiHighlights && index === 0,
      }));

      return (
        <>
          <ResultHeader>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
                <GhostButton onClick={handleBackToList}>
                  ← &nbsp; Quay lại danh sách
                </GhostButton>
              </div>
              <div>
                <TitleSocial>Các top scene nổi bật do AI chọn ra</TitleSocial>
              </div>
              {/* <Title>{getFilename(selectedHistory?.input_path)}</Title> */}
              {/* <Subtitle>
                {hasAiHighlights
                  ? `AI đã chọn ra ${aiHighlightItems.length} highlight nổi bật nhất.`
                  : `Video của bạn đã được phân tích thành ${aiHighlightItems.length} cảnh.`}
              </Subtitle> */}
            </div>
          </ResultHeader>

          <ResultGrid>
            {aiHighlightItems.map((item, index) => (
              <HighlightCard key={item.id} $featured={index === 0}>
                <HighlightVisual $featured={index === 0}>
                  {activeClipId === item.previewId && clipUrls[item.previewId] && (
                    <InlineVideoOverlay>
                      <InlineVideo
                        autoPlay
                        controls
                        playsInline
                        poster={item.image || undefined}
                        src={clipUrls[item.previewId]}
                      />
                    </InlineVideoOverlay>
                  )}
                  {item.image ? (
                    <HighlightImage alt={item.title} src={item.image} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    </div>
                  )}
                  {loadingClipId === item.previewId && (
                    <VideoLoadingBadge>Đang tải clip...</VideoLoadingBadge>
                  )}
                  <HighlightOverlay>
                    <div>
                      <HighlightMeta>
                        {index === 0 && <Tag>Nổi bật nhất</Tag>}
                        {item.rank && <Tag $tone="rgba(120, 168, 255, 0.16)" $color="#d6e5ff">Top {item.rank}</Tag>}
                      </HighlightMeta>
                    </div>
                    {activeClipId !== item.previewId && (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                        {item.clipPath && (
                          <PlayButton type="button" onClick={() => handlePlayInline(item)}>
                            <PlayIcon />
                          </PlayButton>
                        )}
                        <div>
                          <div style={{ fontWeight: 800 }}>{item.title}</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.68)' }}>
                            Thời lượng: {item.duration}
                          </div>
                        </div>
                      </div>
                    )}
                  </HighlightOverlay>
                </HighlightVisual>
                <HighlightContent>
                  <HighlightTopRow>
                    <HighlightTitleBlock>
                      <HighlightHeadline>{item.headline || item.title}</HighlightHeadline>
                    </HighlightTitleBlock>
                  </HighlightTopRow>
                  {item.whyItStandsOut && (
                    <InsightSection>
                      <InsightLabel>Vì sao AI chọn đoạn này</InsightLabel>
                      <InsightText>{item.whyItStandsOut}</InsightText>
                    </InsightSection>
                  )}
                  {item.editorialUse && (
                    <InsightSection>
                      <InsightLabel>Gợi ý sử dụng</InsightLabel>
                      <InsightText>{item.editorialUse}</InsightText>
                    </InsightSection>
                  )}
                </HighlightContent>
              </HighlightCard>
            ))}
          </ResultGrid>

          <Divider />

          <ResultHeader>
            <TitleSocial>Format Dọc Social</TitleSocial>
          </ResultHeader>

          <VerticalVideoGrid>
            {aiHighlightItems.map((item, index) => {
              const verticalId = `vertical-${item.previewId}`;
              const isPlaying = activeVerticalVideoId === verticalId;
              const isLoading = loadingVerticalClipId === verticalId;

              return (
                <VerticalVideoCard key={verticalId}>
                  <VerticalVideoContainer>
                    {item.image && (
                      <VerticalVideoPoster
                        src={item.image}
                        alt={item.title}
                        $hidden={isPlaying && verticalClipUrls[verticalId]}
                      />
                    )}

                    {verticalClipUrls[verticalId] && (
                      <VerticalVideo
                        ref={(el) => { verticalVideoRefs.current[verticalId] = el; }}
                        src={verticalClipUrls[verticalId]}
                        playsInline
                        loop
                        onLoadedData={(e) => {
                          if (activeVerticalVideoId === verticalId) {
                            e.target.play().catch(console.error);
                          }
                        }}
                        onEnded={() => {
                          setActiveVerticalVideoId(null);
                        }}
                        onClick={() => {
                          const video = verticalVideoRefs.current[verticalId];
                          if (video) {
                            if (video.paused) {
                              video.play();
                              setActiveVerticalVideoId(verticalId);
                            } else {
                              video.pause();
                              setActiveVerticalVideoId(null);
                            }
                          }
                        }}
                      />
                    )}

                    {isLoading && <VerticalLoadingSpinner />}

                    <VerticalDownloadButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadVerticalVideo(item);
                      }}
                      title="Tải xuống video"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                      </svg>
                    </VerticalDownloadButton>

                    <VerticalPlayButton
                      $playing={isPlaying}
                      onClick={() => handleVerticalVideoPlay(item)}
                    >
                      {isPlaying ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <PlayIcon />
                      )}
                    </VerticalPlayButton>

                    <VerticalVideoOverlay>
                      <VerticalVideoInfo>
                        <VerticalVideoTitle>{item.headline || item.title}</VerticalVideoTitle>
                        <VerticalVideoMeta>
                          <VerticalMetaBadge>{item.duration}</VerticalMetaBadge>
                          {/* {item.rank && (
                            <VerticalMetaBadge>Top {item.rank}</VerticalMetaBadge>
                          )}
                          {typeof item.score === 'number' && (
                            <VerticalMetaBadge>Score {item.score}</VerticalMetaBadge>
                          )} */}
                        </VerticalVideoMeta>
                      </VerticalVideoInfo>
                    </VerticalVideoOverlay>
                  </VerticalVideoContainer>
                </VerticalVideoCard>
              );
            })}
          </VerticalVideoGrid>

          <Divider />

          <ResultHeader>
            <TitleSocial>Format Dọc Social Center</TitleSocial>
          </ResultHeader>

          <VerticalVideoGrid>
            {aiHighlightItems.map((item, index) => {
              const centerId = `center-${item.previewId}`;
              const isPlaying = activeCenterVideoId === centerId;
              const isLoading = loadingCenterClipId === centerId;

              return (
                <VerticalVideoCard key={centerId}>
                  <VerticalVideoContainer>
                    {item.image && (
                      <VerticalVideoPoster
                        src={item.image}
                        alt={item.title}
                        $hidden={isPlaying && centerClipUrls[centerId]}
                      />
                    )}

                    {centerClipUrls[centerId] && (
                      <VerticalVideo
                        ref={(el) => { centerVideoRefs.current[centerId] = el; }}
                        src={centerClipUrls[centerId]}
                        playsInline
                        loop
                        onLoadedData={(e) => {
                          if (activeCenterVideoId === centerId) {
                            e.target.play().catch(console.error);
                          }
                        }}
                        onEnded={() => {
                          setActiveCenterVideoId(null);
                        }}
                        onClick={() => {
                          const video = centerVideoRefs.current[centerId];
                          if (video) {
                            if (video.paused) {
                              video.play();
                              setActiveCenterVideoId(centerId);
                            } else {
                              video.pause();
                              setActiveCenterVideoId(null);
                            }
                          }
                        }}
                      />
                    )}

                    {isLoading && <VerticalLoadingSpinner />}

                    <VerticalDownloadButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadCenterVideo(item);
                      }}
                      title="Tải xuống video"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                      </svg>
                    </VerticalDownloadButton>

                    <VerticalPlayButton
                      $playing={isPlaying}
                      onClick={() => handleCenterVideoPlay(item)}
                    >
                      {isPlaying ? (
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                      ) : (
                        <PlayIcon />
                      )}
                    </VerticalPlayButton>

                    <VerticalVideoOverlay>
                      <VerticalVideoInfo>
                        <VerticalVideoTitle>{item.headline || item.title}</VerticalVideoTitle>
                        <VerticalVideoMeta>
                          <VerticalMetaBadge>{item.duration}</VerticalMetaBadge>
                        </VerticalVideoMeta>
                      </VerticalVideoInfo>
                    </VerticalVideoOverlay>
                  </VerticalVideoContainer>
                </VerticalVideoCard>
              );
            })}
          </VerticalVideoGrid>

          <Divider />

          <ResultHeader>
            <div>
              <TitleSocial>Tất cả các scene của video</TitleSocial>
            </div>
          </ResultHeader>

          <ResultGrid>
            {metadataItems.map((item, index) => (
              <HighlightCard key={item.id} $featured={index === 0}>
                <HighlightVisual $featured={index === 0}>
                  {activeClipId === item.previewId && clipUrls[item.previewId] && (
                    <InlineVideoOverlay>
                      <InlineVideo
                        autoPlay
                        controls
                        playsInline
                        poster={item.image || undefined}
                        src={clipUrls[item.previewId]}
                      />
                    </InlineVideoOverlay>
                  )}
                  {item.image ? (
                    <HighlightImage alt={item.title} src={item.image} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    </div>
                  )}
                  {loadingClipId === item.previewId && (
                    <VideoLoadingBadge>Đang tải clip...</VideoLoadingBadge>
                  )}
                  <HighlightOverlay>
                    <div>
                      <HighlightMeta>
                        <Tag $tone="rgba(255, 255, 255, 0.12)">Scene</Tag>
                      </HighlightMeta>
                    </div>
                    {activeClipId !== item.previewId && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 12,
                        }}
                      >
                        {item.clipPath && (
                          <PlayButton type="button" onClick={() => handlePlayInline(item)}>
                            <PlayIcon />
                          </PlayButton>
                        )}
                        <div>
                          <div style={{ fontWeight: 800 }}>{item.title}</div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'rgba(255,255,255,0.68)',
                            }}
                          >
                            {item.duration}
                          </div>
                        </div>
                      </div>
                    )}
                  </HighlightOverlay>
                </HighlightVisual>
                <HighlightContent>
                  <HighlightTopRow>
                    <HighlightTitleBlock>
                      <HighlightHeadline>{item.headline || item.title}</HighlightHeadline>
                      <HighlightMeta>
                        <MetaPill>{item.duration}</MetaPill>
                      </HighlightMeta>
                    </HighlightTitleBlock>
                  </HighlightTopRow>

                  {/* {item.transcript && (
                    <div>
                      <div style={{ marginBottom: 8, fontSize: 11, color: 'var(--accent)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Transcript
                      </div>
                      <TextRow>
                        <TruncatedText $expanded={expandedItems[`${item.id}-transcript`]} $type="transcript">
                          "{item.transcript}"
                        </TruncatedText>
                        <ExpandButton onClick={() => toggleExpand(item.id, 'transcript')}>
                          {expandedItems[`${item.id}-transcript`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </ExpandButton>
                      </TextRow>
                    </div>
                  )} */}
                  {item.description && (
                    <div style={{ marginTop: item.transcript ? 12 : 0 }}>
                      <TextRow>
                        <TruncatedText $expanded={expandedItems[`${item.id}-description`]} $type="description">
                          {item.description}
                        </TruncatedText>
                        <ExpandButton onClick={() => toggleExpand(item.id, 'description')}>
                          {expandedItems[`${item.id}-description`] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </ExpandButton>
                      </TextRow>
                    </div>
                  )}
                </HighlightContent>
              </HighlightCard>
            ))}
          </ResultGrid>
        </>
      );
    }

    // Default: show list view
    return (
      <>
        <HistoryHeader>
          <div>
            <Eyebrow>Thư viện nội dung</Eyebrow>
            <Title style={{ fontSize: 'clamp(34px, 4vw, 50px)', marginBottom: 10 }}>Lịch sử video đã xử lý</Title>
            <Subtitle>
              Xem lại các video đã tải lên, kiểm tra trạng thái hiện tại và mở chi tiết để theo dõi nhanh chóng.
            </Subtitle>
          </div>
          <FilterRow>
            <GhostButton onClick={fetchHistory}>
              Làm mới
            </GhostButton>
          </FilterRow>
        </HistoryHeader>

        <TableCard>
          <HistoryTable>
            <THead>
              <tr>
                <TH>Video</TH>
                <TH>Ngày tải</TH>
                <TH>Số cảnh</TH>
                <TH>Trạng thái</TH>
                <TH>Tiến độ</TH>
                <TH $right>Thao tác</TH>
              </tr>
            </THead>
            <tbody>
              {isLoadingHistory ? (
                <tr>
                  <TD colSpan="6" style={{ textAlign: 'center', color: 'var(--muted)' }}>
                    Đang tải lịch sử...
                  </TD>
                </tr>
              ) : filteredHistory.length ? (
                filteredHistory.map((item, index) => (
                  <TR
                    key={item.job_id}
                    $active={item.job_id === selectedHistory?.job_id}
                    onClick={async () => {
                      if (item.status === 'succeeded') {
                        await handleViewInStudio(item);
                      } else {
                        handleSelectHistoryItem(item);
                      }
                    }}
                    style={{ cursor: item.status === 'succeeded' ? 'pointer' : 'default' }}
                  >
                    <TD>
                      <VideoCell>
                        <ThumbWrap>
                          {historyThumbnails[item.job_id] ? (
                            <Thumbnail src={historyThumbnails[item.job_id]} alt={getFilename(item.input_path)} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <p>{index + 1}</p>
                            </div>
                          )}
                        </ThumbWrap>
                        <div>
                          <VideoName>{getFilename(item.input_path)?.substring(0, 20)}{getFilename(item.input_path)?.length > 20 ? '...' : ''}</VideoName>
                          <VideoMeta>
                            Mã: {item.job_id.substring(0, 8)}...
                          </VideoMeta>
                        </div>
                      </VideoCell>
                    </TD>
                    <TD $muted>{formatDate(item.created_at)}</TD>
                    <TD $muted>{item.metadata_count || 0}</TD>
                    <TD>
                      <StatusPill $status={item.status === 'processing' || item.status === 'queued' || item.status === 'running' ? 'processing' : 'completed'}>
                        <StatusDot $status={item.status === 'processing' || item.status === 'queued' || item.status === 'running' ? 'processing' : 'completed'} />
                        {item.status}
                      </StatusPill>
                    </TD>
                    <TD $muted>
                      {item.progress?.percent != null ? `${item.progress.percent}%` : '--'}
                    </TD>
                    <TD>
                      <ActionWrap>
                        <ActionButton
                          disabled={item.status !== 'succeeded'}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleViewInStudio(item);
                          }}
                        >
                          Xem
                        </ActionButton>
                        <ActionButton
                          $danger
                          disabled={deletingJobId === item.job_id}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteJob(item);
                          }}
                        >
                          {deletingJobId === item.job_id ? 'Đang xóa...' : 'Xóa'}
                        </ActionButton>
                      </ActionWrap>
                    </TD>
                  </TR>
                ))
              ) : (
                <tr>
                  <TD colSpan="6" style={{ textAlign: 'center', color: 'var(--muted)' }}>
                    Không tìm thấy video nào.
                  </TD>
                </tr>
              )}
            </tbody>
          </HistoryTable>

          <TableFooter>
            <FooterHint>
              Hiển thị {filteredHistory.length} / {jobHistory.length} video
            </FooterHint>
          </TableFooter>
        </TableCard>
      </>
    );
  };

  if (!isAuthenticated()) {
    return <Redirect to={ROUTES.SIGN_IN} />;
  }

  return (
    <Page>
      <DashboardGlobalStyle />
      <HiddenInput accept="video/mp4,.mp4" onChange={handleFileChange} ref={fileInputRef} type="file" />

      <Sidebar>

        <BrandRow>
          <UserCard onClick={handleDashboardRefresh} role="button" tabIndex={0} onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleDashboardRefresh();
            }
          }}>
            <Avatar
              alt="Hệ thống AI"
              src="https://res.cloudinary.com/farmcode/image/upload/v1762243951/other/jn4inzdqkx3cgidozthr.png"
            />
            <UserMeta>
              <UserName>Video Highlight</UserName>
              <UserHint>Lumination AI</UserHint>
            </UserMeta>
          </UserCard>
        </BrandRow>

        <NavGroup>
          <NavButton type="button" $active={activeTab === 'process'} onClick={() => setActiveTab('process')}>
            Studio
          </NavButton>
          <NavButton type="button" $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
            Lịch sử
          </NavButton>
        </NavGroup>
      </Sidebar>

      <Main>
        <TopBar>
          <TopActions>
            <GhostButton type="button" onClick={handleLogout}>
              Đăng xuất
            </GhostButton>
          </TopActions>
        </TopBar>

        <Content>{activeTab === 'process' ? renderProcessContent() : renderHistoryContent()}</Content>
      </Main>

      {deleteConfirmJob && (
        <ModalOverlay onClick={cancelDelete}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Xác nhận xóa</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <ModalText>Bạn có chắc chắn muốn xóa job này không?</ModalText>
              <ModalFilename>{getFilename(deleteConfirmJob.input_path)}</ModalFilename>
              <ModalText style={{ marginTop: '12px', fontSize: '13px', color: 'var(--subtle)' }}>
                ⚠️ Hành động này không thể hoàn tác.
              </ModalText>
            </ModalBody>
            <ModalActions>
              <ModalButton onClick={cancelDelete}>Hủy</ModalButton>
              <ModalButton $primary onClick={confirmDelete}>Xóa</ModalButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}

      {isScriptModalOpen && pendingUploadFile && (
        <ModalOverlay onClick={isSubmittingUpload ? undefined : handleSkipScript}>
          <Modal as="form" onClick={(e) => e.stopPropagation()} onSubmit={handleScriptSubmit}>
            <ModalHeader>
              <ModalTitle>Cấu hình xử lý video</ModalTitle>
            </ModalHeader>
            <ModalBody>
              <ModalText>
                Chọn category trước khi gửi video. Nếu chọn `phim`, bạn có thể đính kèm thêm file kịch bản để AI có thêm ngữ cảnh khi phân tích highlight.
              </ModalText>
              <ModalFilename>{pendingUploadFile.name}</ModalFilename>
              <CategorySelector>
                {['phim', 'esport', 'football'].map((category) => (
                  <CategoryOption
                    key={category}
                    $active={selectedUploadCategory === category}
                    disabled={isSubmittingUpload}
                    onClick={() => {
                      setSelectedUploadCategory(category);
                      if (category !== 'phim') {
                        setScriptFile(null);
                      }
                    }}
                    type="button"
                  >
                    {category}
                  </CategoryOption>
                ))}
              </CategorySelector>
              {selectedUploadCategory === 'phim' && (
                <>
                  <ScriptFileInput
                    accept=".txt,.pdf"
                    disabled={isSubmittingUpload}
                    id="script-file-input"
                    onChange={(event) => {
                      const f = event.target.files && event.target.files[0];
                      if (!f) return;
                      if (f.size > 10 * 1024 * 1024) {
                        alert('File kịch bản không được vượt quá 10MB.');
                        event.target.value = '';
                        return;
                      }
                      setScriptFile(f);
                    }}
                    type="file"
                  />
                  <ScriptFileLabel htmlFor="script-file-input" $hasFile={!!scriptFile}>
                    {scriptFile ? scriptFile.name : 'Chọn file .txt hoặc .pdf (tối đa 10MB)'}
                  </ScriptFileLabel>
                </>
              )}
            </ModalBody>
            <ModalActions>
              <ModalButton disabled={isSubmittingUpload} onClick={handleSkipScript} type="button">
                Bỏ qua
              </ModalButton>
              <ModalButton $primary disabled={isSubmittingUpload} type="submit">
                {isSubmittingUpload ? 'Đang tải lên hệ thống...' : 'Submit'}
              </ModalButton>
            </ModalActions>
          </Modal>
        </ModalOverlay>
      )}
    </Page>
  );
}

export default Dashboard;
